import {css, html, LitElement, nothing} from "lit";
import {pxToRem} from "../styles";
import {PropertyValues} from "@lit/reactive-element";
import {WlTextValueAdded} from "../events/WlTextValueAdded";

export class WlTextInput extends LitElement {
    private _internals: ElementInternals;
    public value: string | null;
    public placeholder: string;
    public input: HTMLInputElement | null;
    public title: string;
    public label: string | null;
    private isRequired: boolean = false;
    public icon: string | null;
    public iconActive: string | null;

    static formAssociated = true; // This is required to use the this._internals.setFormValue() method

    static get properties() {
        return {
            value: {
                type: String, reflect: true, converter: {
                    fromAttribute(value: string) {
                        if (value == "") {
                            return null
                        }
                        return value
                    },
                }
            },
            placeholder: {type: String, reflect: true},
            title: {type: String, reflect: true},
            label: {type: String, attribute: false},
            isRequired: {type: Boolean},
            icon: {type: String},
            iconActive: {type: String},
        }
    }

    constructor() {
        super();
        // @ts-ignore
        this._internals = this.attachInternals();
        this.value = null;
        this.placeholder = "";
        this.label = ""
        this.title = "";
        this.input = null;
        this.icon = null;
        this.iconActive = null;
    }

    static get styles() {
        return css`
            :host {
                display: flex;
                flex-direction: column;
                gap: var(--wl-text-input-gap, ${pxToRem(16)});
            }

            .title {
                font-size: var(--wl-range-item-label-font-size, ${pxToRem(14)});
                font-weight: var(--wl-range-item-label-font-weight, 700);
                color: var(--wl-range-item-label-color, #000000);
                padding: var(--wl-text-input-title-padding, ${pxToRem(10)});
            }
            
            .input-wrapper {
                display: flex;
                align-items: center;
                border: 1px solid var(--wl-text-input-border-color, #000);
                gap: var(--wl-text-input-wrapper-gap, ${pxToRem(5)});
                border-radius: ${pxToRem(10)};
                max-width: 100%;
                padding: ${pxToRem(8)};
                justify-content: space-between;
                
                input {
                    padding: unset;
                    border: unset;
                    border-radius: unset;
                }
            }
            
            .icons-wrapper {
                display: flex;
                align-items: center;
                justify-content: space-between;
                gap: 4px;

                .icon {
                    display: flex;
                }
            }

            input {
                width: 100%;
                height: var(--wl-range-item-height, ${pxToRem(30)});
                font-size: ${pxToRem(14)};
                color: var(--wl-text-input-color, #000);
                border: var(--wl-text-input-border, 1px solid #000);
                border-radius: var(--wl-text-input-border-radius, ${pxToRem(10)});
                padding: var(--wl-text-input-padding, ${pxToRem(8)});
                
            }

            input:focus.error {
                border-color: #ff0000;
            }

            input:focus::-webkit-input-placeholder {
                opacity: 0;
            }

            input:placeholder-shown {
                text-overflow: ellipsis;
            }
            
            input:focus-visible {
                text-overflow: ellipsis;
                outline: none;
            }
        `
    }

    protected render() {
        if (this.icon != null) {
            return html`
            ${this._renderLabel()}
            <div class="input-wrapper">
                <input type="text" value="${this.label}" autocomplete="off" placeholder="${this.placeholder}"
                       @change="${this.updateValueFromInput}" @keyup="${this.updateValueFromInput}" @keydown="${this.dispatchEventOnEnter}"
                />
                <div class="icons-wrapper">
                    <slot></slot>
                    ${this._renderIcon()}
                </div>
            </div>
        `
        }

        return html`
            ${this._renderLabel()}
            <input type="text" value="${this.label}" autocomplete="off" placeholder="${this.placeholder}"
                   @change="${this.updateValueFromInput}" @keyup="${this.updateValueFromInput}"
            />
        `
    }


    protected update(_changedProperties: PropertyValues) {
        super.update(_changedProperties);
        this.label = this.value
        this._internals.setFormValue(this.value?.toString() || null);
    }

    protected updated(_changedProperties: PropertyValues) {
        super.updated(_changedProperties);
        if (this.input != null) {
            if (this.label != null) {
                this.input!!.value = this.label
            }
        }

        if (_changedProperties.has("value")) {
            if (this.isInvalidValue()) {
                return;
            }

            this.dispatchEvent(new CustomEvent("wl-change", {bubbles: true, composed: true}));
        }
    }

    protected firstUpdated(_changedProperties: PropertyValues) {
        this.input = this.renderRoot.querySelector("input")!;
    }

    private _renderLabel() {
        if (this.title != '') {
            return html`<label class="title">${this.title}</label>`;
        }
        return nothing;
    }


    private _renderIcon() {
        const inputValueNotEmpty = this.value && this.value.trim() !== '';
        const iconToRender = inputValueNotEmpty ? this.iconActive : this.icon;
        return html `<div class="icon"><wl-icon icon=${iconToRender}></wl-icon></div>`;
    }

    clear() {
        this.value = null
    }

    checkValidity(): boolean {
        return this._internals.checkValidity();
    }

    private dispatchEventOnEnter(event: KeyboardEvent) {
        if (event.key === "Enter") {
            if (this.input != null) {
                this.dispatchEvent(new WlTextValueAdded(this.input));
            }
        }
    }

    private updateValueFromInput() {
        this.value = this.input?.value || null;
    }

    private isInvalidValue() {
        return this.isRequired && this.value == null;
    }
}
